/**
 * React Control Registry
 * Unified registry that combines plugin metadata (DefaultControlRegistry) with
 * React component references (IControlComponentRegistry) into a single registration call.
 *
 * Eliminates the duplicate-registration burden: call `registerReact(plugin)` once
 * instead of separately calling DefaultControlRegistry.register() and
 * ControlComponentRegistry.register().
 *
 * Implements:
 * - ReactPluginAdapter  — registerReact / getReactPlugin / getAllReactPlugins
 * - IControlComponentRegistry — getComponent / register / unregister / has
 *   (so it can be passed directly as the `registry` prop on ControlRenderer)
 */

import { DefaultControlRegistry } from '@atlas-forms/control-registry-js';
import type {
  FormControlType,
  ReactControlPlugin,
  ReactPluginAdapter,
} from '@atlas-forms/types-js';
import React from 'react';
import type { IControlComponentRegistry, ControlRendererProps } from './ControlRenderer';

export class ReactControlRegistry
  extends DefaultControlRegistry
  implements ReactPluginAdapter, IControlComponentRegistry
{
  private reactPlugins: Map<FormControlType, ReactControlPlugin> = new Map();

  // ── ReactPluginAdapter ───────────────────────────────────────────────────────

  /**
   * Register a plugin together with its React components in a single call.
   * Replaces the two-step: DefaultControlRegistry.register() + ControlComponentRegistry.register().
   */
  registerReact(plugin: ReactControlPlugin): void {
    this.register(plugin);
    this.reactPlugins.set(plugin.type, plugin);
  }

  getReactPlugin(type: FormControlType): ReactControlPlugin | undefined {
    return this.reactPlugins.get(type);
  }

  getAllReactPlugins(): ReactControlPlugin[] {
    return Array.from(this.reactPlugins.values());
  }

  // ── IControlComponentRegistry ────────────────────────────────────────────────

  /**
   * Get the React component for a control type.
   * Mode-aware: returns ViewComponent / DesignComponent / EditComponent as appropriate.
   * Falls back to a component registered via the legacy `register()` path.
   */
  getComponent(
    type: string,
    mode: string = 'edit',
  ): React.ComponentType<ControlRendererProps> | undefined {
    const plugin = this.reactPlugins.get(type as FormControlType);
    if (plugin) {
      if (mode === 'view' || mode === 'preview') {
        return (plugin.ViewComponent ?? plugin.EditComponent) as React.ComponentType<ControlRendererProps>;
      }
      if (mode === 'design') {
        return (plugin.DesignComponent ?? plugin.EditComponent) as React.ComponentType<ControlRendererProps>;
      }
      return plugin.EditComponent as React.ComponentType<ControlRendererProps>;
    }
    // Legacy fallback
    return this.legacyComponents.get(type);
  }

  // Legacy direct-component registration (backward compatibility)
  private legacyComponents: Map<string, React.ComponentType<ControlRendererProps>> = new Map();

  /**
   * Register a React component directly (legacy path, kept for backward compatibility).
   * Prefer `registerReact()` for new code.
   */
  override register(
    typeOrPlugin: any,
    component?: any,
  ): void {
    if (typeof typeOrPlugin === 'string') {
      // Legacy: register(type, component)
      if (component) {
        this.legacyComponents.set(typeOrPlugin, component);
      }
    } else {
      // Standard ControlPlugin registration via parent
      super.register(typeOrPlugin);
    }
  }

  unregister(type: string): void {
    this.legacyComponents.delete(type);
    try {
      super.unregister(type as FormControlType);
    } catch {
      // Not registered as a plugin — that's fine
    }
    this.reactPlugins.delete(type as FormControlType);
  }

  has(type: string): boolean {
    return (
      this.reactPlugins.has(type as FormControlType) ||
      this.legacyComponents.has(type) ||
      super.has(type as FormControlType)
    );
  }

  /**
   * Get all registered components as a map of type → component.
   * Merges react-plugin EditComponents and legacy direct registrations.
   * Legacy registrations take precedence for the same type.
   */
  override getAll(): any {
    const result = new Map<string, React.ComponentType<ControlRendererProps>>();

    for (const [type, plugin] of this.reactPlugins) {
      if (plugin.EditComponent) {
        result.set(type, plugin.EditComponent as React.ComponentType<ControlRendererProps>);
      }
    }

    for (const [type, component] of this.legacyComponents) {
      result.set(type, component);
    }

    return result;
  }

  // ── Reset (for testing) ──────────────────────────────────────────────────────

  clear(): void {
    super.clear();
    this.reactPlugins.clear();
    this.legacyComponents.clear();
  }
}

// ── Global singleton ─────────────────────────────────────────────────────────

let globalReactRegistry: ReactControlRegistry | null = null;

/**
 * Get the global unified React control registry.
 * This is the single source of truth for both plugin metadata and React components.
 */
export function getGlobalReactRegistry(): ReactControlRegistry {
  if (!globalReactRegistry) {
    globalReactRegistry = new ReactControlRegistry();
  }
  return globalReactRegistry;
}

/**
 * Reset the global registry (for testing).
 */
export function resetGlobalReactRegistry(): void {
  globalReactRegistry = null;
}
